import React, { useEffect, useRef } from "react";

// In-canvas text editor. Renders a contentEditable inside a <foreignObject>
// that lives in the same transformed coordinate space as the canvas elements,
// so the editing text sits exactly where the SVG <text> would render (WYSIWYG)
// and a native blinking caret waits for input. Commits on blur / Enter / Escape.
//
// Anti-jitter: the SVG <text> places its *baseline* at `y`, while an HTML box
// places text by its line box. To make the glyphs occupy the exact same pixels
// (so nothing shifts when toggling edit mode) we measure the font's ascent /
// descent and (1) set the editable line-height to the full font box so the
// half-leading is zero, then (2) position the box top at `y - ascent`. The HTML
// baseline then lands precisely on the SVG baseline.

// Reuse one offscreen canvas context for font metric measurement.
let _measureCtx = null;
function fontMetrics(a) {
  const fontSize = Number(a.fontSize) || 32;
  const fallbackAsc = fontSize * 0.8;
  const fallbackDesc = fontSize * 0.2;
  if (typeof document === "undefined") return { ascent: fallbackAsc, descent: fallbackDesc };
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  const weight = a.fontWeight || 400;
  const style = a.fontStyle || "normal";
  const family = a.fontFamily || "IBM Plex Sans";
  _measureCtx.font = `${style} ${weight} ${fontSize}px ${family}`;
  try {
    const m = _measureCtx.measureText(a.text && a.text.length ? a.text : "Mg");
    const ascent = m.fontBoundingBoxAscent || m.actualBoundingBoxAscent || fallbackAsc;
    const descent = m.fontBoundingBoxDescent || m.actualBoundingBoxDescent || fallbackDesc;
    return { ascent, descent };
  } catch {
    return { ascent: fallbackAsc, descent: fallbackDesc };
  }
}

export default function TextEditOverlay({ element, onCommit }) {
  const ref = useRef(null);
  const doneRef = useRef(false);
  const a = element.attrs || {};
  const fontSize = Number(a.fontSize) || 32;
  const anchor = a.textAnchor || "start";

  // Align the HTML text baseline to the SVG text baseline (a.y).
  const { ascent, descent } = fontMetrics(a);
  const lineHeight = ascent + descent;
  const originX = a.x ?? 0;
  const top = (a.y ?? 0) - ascent;

  // Horizontal: grow the box from the same anchor point the SVG text uses, so
  // start/middle/end text never jumps sideways when toggling edit mode.
  const translateX = anchor === "middle" ? "-50%" : anchor === "end" ? "-100%" : "0";
  const textAlign = anchor === "middle" ? "center" : anchor === "end" ? "right" : "left";

  useEffect(() => {
    // Defer focus to the next macrotask so it lands AFTER the mousedown/click
    // that created the editor finishes (otherwise the surface click steals
    // focus and immediately blurs/commits the empty editor). preventScroll
    // stops the browser from nudging the canvas to bring the caret into view,
    // which would visibly shift the text.
    const id = setTimeout(() => {
      const node = ref.current;
      if (!node) return;
      node.focus({ preventScroll: true });
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onCommit(ref.current ? ref.current.innerText : "");
  };

  return (
    <foreignObject x={originX} y={top} width={2000} height={Math.max(lineHeight, 40)} style={{ overflow: "visible" }}>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid="text-edit-input"
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onBlur={finish}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Escape" || (e.key === "Enter" && !e.shiftKey)) {
            e.preventDefault();
            finish();
          }
        }}
        style={{
          display: "inline-block",
          minWidth: 1,
          outline: "none",
          whiteSpace: "pre",
          lineHeight: `${lineHeight}px`,
          padding: 0,
          margin: 0,
          transform: `translateX(${translateX})`,
          transformOrigin: "left top",
          fontSize: `${fontSize}px`,
          fontFamily: a.fontFamily || "IBM Plex Sans",
          fontWeight: a.fontWeight || 400,
          fontStyle: a.fontStyle || "normal",
          letterSpacing: a.letterSpacing != null && a.letterSpacing !== "" ? `${a.letterSpacing}px` : undefined,
          color: a.fill || "#0a0a0a",
          caretColor: a.fill || "#0a0a0a",
          textAlign,
          WebkitUserSelect: "text",
          userSelect: "text",
        }}
      >
        {a.text || ""}
      </div>
    </foreignObject>
  );
}
